import { useCallback, useState } from 'react';
import type { CardInstance, CardType, CardRarity } from '@/types/card';
import {
  CARD_DEFINITIONS,
  DROP_RATES,
  MAX_HAND_SIZE,
  MAX_INVENTORY_SIZE,
} from '@/config/cards';

export function useCardSystem() {
  const [inventory, setInventory] = useState<CardInstance[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('cardInventory') || '[]');
    } catch {
      return [];
    }
  });

  const [hand, setHand] = useState<CardInstance[]>([]);

  // Persist inventory to localStorage
  const saveInventory = useCallback((cards: CardInstance[]) => {
    localStorage.setItem('cardInventory', JSON.stringify(cards));
    setInventory(cards);
  }, []);

  // Generate a unique ID
  const generateId = useCallback(() => {
    return `card_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }, []);

  // Roll a random card after level completion
  const rollDrop = useCallback((): CardInstance | null => {
    const roll = Math.random();
    let cumulative = 0;

    const entries = Object.entries(DROP_RATES) as [
      CardRarity | 'nothing',
      number,
    ][];
    for (const [rarity, rate] of entries) {
      cumulative += rate;
      if (roll <= cumulative) {
        if (rarity === 'nothing') return null;

        // Pick a random card of this rarity
        const eligibleTypes = (
          Object.values(CARD_DEFINITIONS) as {
            type: CardType;
            rarity: CardRarity;
          }[]
        )
          .filter((d) => d.rarity === rarity)
          .map((d) => d.type);

        if (eligibleTypes.length === 0) return null;
        const type =
          eligibleTypes[Math.floor(Math.random() * eligibleTypes.length)];
        return { id: generateId(), type, rarity };
      }
    }
    return null;
  }, [generateId]);

  // Add card to inventory (respects max size)
  const addToInventory = useCallback(
    (card: CardInstance): boolean => {
      if (inventory.length >= MAX_INVENTORY_SIZE) return false;
      saveInventory([...inventory, card]);
      return true;
    },
    [inventory, saveInventory]
  );

  // Select cards from inventory into hand before level
  const selectHand = useCallback(
    (cardIds: string[]) => {
      const selected = cardIds
        .slice(0, MAX_HAND_SIZE)
        .map((id) => inventory.find((c) => c.id === id))
        .filter((c): c is CardInstance => c !== undefined);

      setHand(selected);

      // Remove selected cards from inventory
      const selectedSet = new Set(selected.map((c) => c.id));
      saveInventory(inventory.filter((c) => !selectedSet.has(c.id)));
    },
    [inventory, saveInventory]
  );

  // Play a card (remove from hand, return type for effect application)
  const playCard = useCallback(
    (cardId: string): CardType | null => {
      const card = hand.find((c) => c.id === cardId);
      if (!card) return null;
      setHand((prev) => prev.filter((c) => c.id !== cardId));
      return card.type;
    },
    [hand]
  );

  // Return unplayed cards to inventory after level
  const returnHandToInventory = useCallback(() => {
    if (hand.length > 0) {
      saveInventory([...inventory, ...hand]);
      setHand([]);
    }
  }, [hand, inventory, saveInventory]);

  return {
    inventory,
    hand,
    rollDrop,
    addToInventory,
    selectHand,
    playCard,
    returnHandToInventory,
  };
}
